"use client";
import Link from "next/link";
import { Fragment, useState } from "react";
import {
    RiCalendarEventLine,
    RiTicket2Line,
    RiVideoLine,
} from "react-icons/ri";
import { MdOutlineLocationOn } from "react-icons/md";
import { cn } from "~/utils/fontsConfig";
import { If } from "~/components/v3/Conditional";
import { getFormattedTimeStamp } from "~/utils/time";
import { EventImage } from "~/components/v3/events-components/EventImage";
import { GetHomeEvents } from "../../home/actions";

import GlassButton from "~/components/v4/themed/GlassButton";
import { useSession } from "next-auth/react";
import { UserTicketSheet } from "../[eventSlug]/_components/UserTicketSheet";

type Props = {
    event:
        | GetHomeEvents["hostingEvents"][number]
        | GetHomeEvents["attendingEvents"][number];
    userId?: string;
};

export const EventItem = ({ event, userId }: Props) => {
    const { data: session } = useSession();

    const isCurrentUser = userId === session?.user.id;
    const [isTicketSheetOpen, setIsTicketSheetOpen] = useState(false);

    const attendee = event.isAttending
        ? event?.attendees.find((a) => a.user.id === userId)
        : null;

    if (!event) return null;

    return (
        <Fragment key={event.id + "-event-item"}>
            {/* Desktop only */}
            <div
                className="group hidden w-full gap-[1px] sm:flex"
                key={event.id}
            >
                <div
                    className={cn(
                        "ml-[10px] w-[5px] flex-grow-0",
                        "border-l border-v3-N-450 transition-all group-hover:border-v3-N-400",
                    )}
                />
                <div className="-mr-6 flex w-full justify-between pr-3">
                    {/* Event info link */}
                    <div className="flex h-max w-full flex-1 break-all rounded-[4px] py-3 pl-2 text-start transition-[background-color] duration-300 hover:bg-v3-N-500">
                        <div className="z-10 flex w-full flex-row items-start sm:items-start">
                            {/* Image column - preserves dimensions on all screen sizes */}
                            {event.image && (
                                <Link
                                    href={
                                        event.isHosting
                                            ? `/beta/events/${event.slug}/manage`
                                            : `/beta/events/${event.slug}`
                                    }
                                    target="_blank"
                                    className="relative h-[108px] w-[168px] flex-shrink-0 rounded-[4px]"
                                >
                                    <EventImage
                                        event={event}
                                        options={{
                                            type: "contain",
                                        }}
                                        className="aspect-video rounded-xl"
                                        isReadonly
                                    />
                                </Link>
                            )}
                            {/* Content column - reorganized for mobile */}
                            <div className="mx-2 flex min-w-[100px] flex-1 flex-col">
                                {/* Community/User info */}
                                {event.community ? (
                                    <Link
                                        href={`/beta/${event.community.slug}`}
                                        target="_blank"
                                        style={{
                                            wordBreak: "break-word",
                                        }}
                                        className="flex h-max min-w-0 max-w-[100%] gap-2 overflow-hidden text-ellipsis text-base"
                                    >
                                        <span className="text-sm uppercase text-v3-N-300">
                                            {event.community.name}
                                        </span>
                                    </Link>
                                ) : (
                                    <div
                                        style={{
                                            wordBreak: "break-word",
                                        }}
                                        className="flex h-max min-w-0 max-w-[100%] gap-2 overflow-hidden text-ellipsis text-base"
                                    >
                                        <span className="text-sm uppercase text-v3-N-300">
                                            {event.user?.name}
                                        </span>
                                    </div>
                                )}

                                {/* Event name */}
                                <Link
                                    href={
                                        event.isHosting
                                            ? `/beta/events/${event.slug}/manage`
                                            : `/beta/events/${event.slug}`
                                    }
                                    target="_blank"
                                    className="line-clamp-2 w-full min-w-0 overflow-hidden whitespace-normal break-normal"
                                >
                                    {event.name}
                                </Link>

                                {/* Mobile: Ticket button appears within content column */}
                                <If
                                    condition={
                                        isCurrentUser &&
                                        event.isAttending &&
                                        !!event.isTimeSet &&
                                        !!attendee
                                    }
                                >
                                    <div className="mb-1 mt-1 block sm:hidden">
                                        <GlassButton
                                            size="xs"
                                            variant="dark"
                                            className="w-full text-white"
                                            onClick={() => {
                                                setIsTicketSheetOpen(true);
                                            }}
                                        >
                                            <span className="flex w-full justify-center gap-2">
                                                <RiTicket2Line className="h-4 w-4" />
                                                Ticket details
                                            </span>
                                        </GlassButton>
                                    </div>
                                </If>

                                {/* Event details */}
                                <div className="flex items-center gap-1 text-v3-N-300">
                                    <RiCalendarEventLine className="h-4 w-4" />
                                    <span
                                        className="text-sm"
                                        style={{
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {event.isTimeSet
                                            ? getFormattedTimeStamp(
                                                  event.startTime,
                                              )
                                            : "TBD"}
                                    </span>
                                </div>

                                <If condition={!!event.city}>
                                    <div className="flex items-center gap-1 text-v3-N-300">
                                        <MdOutlineLocationOn className="h-4 w-4" />
                                        <span className="text-sm">
                                            {event.city}
                                        </span>
                                    </div>
                                </If>

                                <If condition={!!event.virtualEventUrl}>
                                    <div className="flex items-center gap-1 text-v3-N-300">
                                        <RiVideoLine className="h-4 w-4" />
                                        <span className="text-sm">
                                            Virtual Event
                                        </span>
                                    </div>
                                </If>

                                {/* Hosts */}
                                <div
                                    className={cn(
                                        "block",
                                        "text-sm",
                                        !event.hosts.length && "hidden",
                                    )}
                                >
                                    <span className="mr-1 text-sm text-v3-N-300">
                                        Hosted by:
                                    </span>
                                    {event.hosts.map((h, index) => (
                                        <Fragment key={h.user.id}>
                                            {h.user.id === userId ? (
                                                <span
                                                    style={{
                                                        wordBreak: "break-word",
                                                    }}
                                                    className="inline whitespace-pre-wrap text-v4-primary"
                                                >
                                                    You
                                                    {index <
                                                        event.hosts.length -
                                                            1 && ", "}
                                                </span>
                                            ) : (
                                                <span
                                                    className="inline"
                                                    style={{
                                                        wordBreak: "break-word",
                                                    }}
                                                >
                                                    {h.user.name}
                                                    {index <
                                                        event.hosts.length -
                                                            1 && ", "}
                                                </span>
                                            )}
                                        </Fragment>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Desktop only: Ticket button */}
                        <If
                            condition={
                                isCurrentUser &&
                                event.isAttending &&
                                !!event.isTimeSet &&
                                !!attendee
                            }
                        >
                            <div className="mr-3 mt-3 hidden flex-shrink-0 sm:block">
                                <GlassButton
                                    size="xs"
                                    variant="dark"
                                    className="text-white"
                                    onClick={() => {
                                        setIsTicketSheetOpen(true);
                                    }}
                                >
                                    <RiTicket2Line className="h-4 w-4" />
                                    Ticket details
                                </GlassButton>
                            </div>
                        </If>
                    </div>
                </div>
            </div>
            {/* Mobile only */}
            <div
                className="group flex w-full gap-[1px] sm:hidden"
                key={event.id + "-event-item-mobile"}
            >
                <div
                    className={cn(
                        "ml-[10px] w-[5px] flex-grow-0",
                        "border-l border-v3-N-450 transition-all group-hover:border-v3-N-400",
                    )}
                />
                <div className="-mr-6 flex w-full justify-between pr-3">
                    {/* Event info link */}
                    <div className="flex h-max w-full flex-1 break-all rounded-[4px] py-3 pl-2 text-start transition-[background-color] duration-300 hover:bg-v3-N-500">
                        <div className="z-10 flex w-full flex-col items-start gap-2 sm:items-start">
                            {/* Image column - preserves dimensions on all screen sizes */}
                            {event.image && (
                                <Link
                                    href={
                                        event.isHosting
                                            ? `/beta/events/${event.slug}/manage`
                                            : `/beta/events/${event.slug}`
                                    }
                                    target="_blank"
                                    className="h-full w-full"
                                >
                                    <EventImage
                                        event={event}
                                        options={{
                                            type: "contain",
                                        }}
                                        className="aspect-video rounded-xl"
                                        isReadonly
                                    />
                                </Link>
                            )}
                            {/* Content column - reorganized for mobile */}
                            <div className="mx-2 flex w-full min-w-[100px] max-w-[95%] flex-1 flex-col">
                                {/* Community/User info */}
                                {event.community ? (
                                    <Link
                                        href={`/beta/${event.community.slug}`}
                                        target="_blank"
                                        style={{
                                            wordBreak: "break-word",
                                        }}
                                        className="flex h-max min-w-0 max-w-[100%] gap-2 overflow-hidden text-ellipsis text-base"
                                    >
                                        <span className="text-sm uppercase text-v3-N-300">
                                            {event.community.name}
                                        </span>
                                    </Link>
                                ) : (
                                    <div
                                        style={{
                                            wordBreak: "break-word",
                                        }}
                                        className="flex h-max min-w-0 max-w-[100%] gap-2 overflow-hidden text-ellipsis text-base"
                                    >
                                        <span className="text-sm uppercase text-v3-N-300">
                                            {event.user?.name}
                                        </span>
                                    </div>
                                )}

                                {/* Event name */}
                                <Link
                                    href={
                                        event.isHosting
                                            ? `/beta/events/${event.slug}/manage`
                                            : `/beta/events/${event.slug}`
                                    }
                                    target="_blank"
                                    className="line-clamp-2 w-full min-w-0 overflow-hidden whitespace-normal break-normal"
                                >
                                    {event.name}
                                </Link>

                                {/* Mobile: Ticket button appears within content column */}
                                <If
                                    condition={
                                        isCurrentUser &&
                                        event.isAttending &&
                                        !!attendee
                                    }
                                >
                                    <div className="mb-1 mt-1 block sm:hidden">
                                        <GlassButton
                                            size="xs"
                                            variant="dark"
                                            className="w-full text-white"
                                            onClick={() => {
                                                setIsTicketSheetOpen(true);
                                            }}
                                        >
                                            <span className="flex w-full justify-center gap-2">
                                                <RiTicket2Line className="h-4 w-4" />
                                                Ticket details
                                            </span>
                                        </GlassButton>
                                    </div>
                                </If>

                                {/* Event details */}
                                <div className="flex items-center gap-1 text-v3-N-300">
                                    <RiCalendarEventLine className="h-4 w-4" />
                                    <span
                                        className="text-sm"
                                        style={{
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {getFormattedTimeStamp(
                                            event?.startTime,
                                        )}
                                    </span>
                                </div>
                                <If condition={!!event.city}>
                                    <div className="flex items-center gap-1 text-v3-N-300">
                                        <MdOutlineLocationOn className="h-4 w-4" />
                                        <span className="text-sm">
                                            {event.city}
                                        </span>
                                    </div>
                                </If>

                                {/* Hosts */}
                                <div
                                    className={cn(
                                        "block",
                                        "text-sm",
                                        !event.hosts.length && "hidden",
                                    )}
                                >
                                    <span className="mr-1 text-sm text-v3-N-300">
                                        Hosted by:
                                    </span>
                                    {event.hosts.map((h, index) => (
                                        <Fragment key={h.user.id}>
                                            {h.user.id === userId ? (
                                                <span
                                                    style={{
                                                        wordBreak: "break-word",
                                                    }}
                                                    className="inline whitespace-pre-wrap text-v4-primary"
                                                >
                                                    You
                                                    {index <
                                                        event.hosts.length -
                                                            1 && ", "}
                                                </span>
                                            ) : (
                                                <span
                                                    className="inline"
                                                    style={{
                                                        wordBreak: "break-word",
                                                    }}
                                                >
                                                    {h.user.name}
                                                    {index <
                                                        event.hosts.length -
                                                            1 && ", "}
                                                </span>
                                            )}
                                        </Fragment>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {attendee && (
                <UserTicketSheet
                    event={event}
                    attendee={attendee}
                    isOpen={isTicketSheetOpen}
                    setIsOpen={setIsTicketSheetOpen}
                />
            )}
        </Fragment>
    );
};
